import * as Excel from "exceljs";
import FileSaver from "file-saver";
import { models } from "types/api/viewModels";
import { getFileNameBasedOnCurrentDateTime } from "utils/file";
import { checkForAndReplaceNullEmptyWhitespace, convertToTitleCase } from "utils/string";
import { dateFormat } from "utils/date";
import { UnavailableDataPlaceholderText } from "constants/text";
import { getModificationsFromLineItem, getSingleOrderGlobal } from "pages/OrderPages/utils/OrderDetailUtil.ts";
import { calculateRemainingCubesForExcel } from "utils/trucks.ts";

export function convertTrucksHeaderToExcel(truck: models["TruckViewModel"] | null | undefined) {
	return {
		"Truck Number": "PENDING",
		"Truck Name": checkForAndReplaceNullEmptyWhitespace(truck?.truckName),
		"Account Number": checkForAndReplaceNullEmptyWhitespace(truck?.accountNumber),
		"Account Name": checkForAndReplaceNullEmptyWhitespace(truck?.accountName),
		"Product Line": checkForAndReplaceNullEmptyWhitespace(truck?.productLine?.description),
		"Truck Status": checkForAndReplaceNullEmptyWhitespace(convertToTitleCase(truck?.truckStatus)),
		"Order Date": checkForAndReplaceNullEmptyWhitespace(dateFormat(String(new Date().toDateString()))),
		"Requested Delivery Date": checkForAndReplaceNullEmptyWhitespace(
			dateFormat(String(truck?.requestedDeliveryDate))
		),
		"Cabinet Count": truck?.cabinetCount ?? UnavailableDataPlaceholderText,
		"Cubes Count": truck?.cubeCount ?? UnavailableDataPlaceholderText,
		"Cubes Available":
			calculateRemainingCubesForExcel(truck?.cubeCount, truck?.cubeCapacity) ?? UnavailableDataPlaceholderText
	};
}

export function convertTruckStopsToExcel(truck: models["TruckViewModel"] | null | undefined) {
	return truck?.stops?.map((stop, index) => ({
		"Stop Number": checkForAndReplaceNullEmptyWhitespace(index + 1),
		"Address Name": checkForAndReplaceNullEmptyWhitespace(stop?.address?.name),
		"Address 1": checkForAndReplaceNullEmptyWhitespace(stop?.address?.line1),
		"Address 2": checkForAndReplaceNullEmptyWhitespace(stop?.address?.line2),
		City: checkForAndReplaceNullEmptyWhitespace(stop?.address?.city),
		State: checkForAndReplaceNullEmptyWhitespace(stop?.address?.state),
		Zip: checkForAndReplaceNullEmptyWhitespace(stop?.address?.zip)
	}));
}

export function convertTruckOrdersToExcel(truck: models["TruckViewModel"] | undefined | null) {
	return truck?.stops?.flatMap((stop, index) => {
		const truckOrders = stop?.draftOrders ?? [];
		return truckOrders.map((order) => {
			const truckOrderGlobal = getSingleOrderGlobal(order);
			const truckOrderConfigurations = order.configurations?.flatMap((config) => config.lineItems);
			const totalCabinetCount =
				truckOrderConfigurations
					?.reduce((sum, item) => sum + (item?.cabinetCount ?? 0), 0)
					.toFixed(2)
					.replace(/[.,]00$/, "") ?? UnavailableDataPlaceholderText;
			const totalAccessoryCount =
				truckOrderConfigurations
					?.reduce((sum, item) => sum + (item?.accessoryCount ?? 0), 0)
					.toFixed(2)
					.replace(/[.,]00$/, "") ?? UnavailableDataPlaceholderText;
			const totalCubeCount =
				truckOrderConfigurations
					?.reduce((sum, item) => sum + (item?.cubeCount ?? 0), 0)
					.toFixed(2)
					.replace(/[.,]00$/, "") ?? UnavailableDataPlaceholderText;

			return {
				"Stop Number": checkForAndReplaceNullEmptyWhitespace(index + 1),
				"Order Number": "PENDING",
				"PO Number": checkForAndReplaceNullEmptyWhitespace(order?.poNumber),
				"Job Name": checkForAndReplaceNullEmptyWhitespace(order?.jobName),
				"Order Type": checkForAndReplaceNullEmptyWhitespace(order?.orderType),
				"Designer Number": checkForAndReplaceNullEmptyWhitespace(order?.designerNumber),
				"Door Style": checkForAndReplaceNullEmptyWhitespace(truckOrderGlobal?.style?.description),
				Finish: checkForAndReplaceNullEmptyWhitespace(truckOrderGlobal?.finish?.description),
				"Cabinet Count": totalCabinetCount,
				"Accessory Count": totalAccessoryCount,
				"Cube Count": totalCubeCount,
				"Ship To Phone": checkForAndReplaceNullEmptyWhitespace(order?.shipToAddress?.phoneNumber),
				"Ship To Email": checkForAndReplaceNullEmptyWhitespace(order?.shipToAddress?.email),
				"Shipping Label Notes": checkForAndReplaceNullEmptyWhitespace(order?.labelComments)
			};
		});
	});
}

export function convertTruckOrderLineItemsToExcel(truck: models["TruckViewModel"] | undefined | null) {
	const truckOrders = truck?.stops?.flatMap((stop) => stop?.draftOrders);
	return truckOrders?.flatMap((order) => {
		const truckOrderGlobal = getSingleOrderGlobal(order);
		return order?.configurations
			?.flatMap((config) =>
				config?.lineItems?.flatMap((lineItem) => ({
					"Order Number": "PENDING",
					"Line Item Number": checkForAndReplaceNullEmptyWhitespace(lineItem?.lineItemNumber),
					"Quantity Ordered": checkForAndReplaceNullEmptyWhitespace(lineItem?.quantityOrdered),
					"Part/SKU": checkForAndReplaceNullEmptyWhitespace(lineItem?.sku),
					Description: checkForAndReplaceNullEmptyWhitespace(lineItem?.description),
					"Door Style": checkForAndReplaceNullEmptyWhitespace(truckOrderGlobal?.style?.description),
					Finish: checkForAndReplaceNullEmptyWhitespace(truckOrderGlobal?.finish?.description),
					Modification: checkForAndReplaceNullEmptyWhitespace(
						getModificationsFromLineItem(lineItem).toString()
					)
				}))
			)
			.flat();
	});
}

export async function generateTruckConfirmationPageExcel(truck: models["TruckViewModel"] | undefined | null) {
	const workbook = new Excel.Workbook();
	const trucksHeaderWorksheet = workbook.addWorksheet("Truck");
	const truckStopsWorksheet = workbook.addWorksheet("Stops");
	const truckOrdersWorksheet = workbook.addWorksheet("Orders");
	const truckLineItemsWorksheet = workbook.addWorksheet("Line Items");

	const trucksHeaderInfo = convertTrucksHeaderToExcel(truck);
	const trucksStopInfo = convertTruckStopsToExcel(truck);
	const truckOrderInfo = convertTruckOrdersToExcel(truck);
	const truckOrderLineItemInfo = convertTruckOrderLineItemsToExcel(truck);

	if (trucksHeaderInfo) {
		trucksHeaderWorksheet.columns = Object.keys(trucksHeaderInfo ?? {}).map((col) => {
			return { key: col, header: col };
		});
		trucksHeaderWorksheet.addRow(trucksHeaderInfo);
	}

	if (trucksStopInfo && trucksStopInfo?.length > 0) {
		truckStopsWorksheet.columns = Object.keys(trucksStopInfo[0] ?? {}).map((col) => {
			return { key: col, header: col };
		});
		truckStopsWorksheet.addRows(trucksStopInfo);
	}

	if (truckOrderInfo && truckOrderInfo.length > 0) {
		truckOrdersWorksheet.columns = Object.keys(truckOrderInfo[0] ?? {}).map((col) => {
			return { key: col, header: col };
		});
		truckOrdersWorksheet.addRows(truckOrderInfo);
	}

	if (truckOrderLineItemInfo && truckOrderLineItemInfo.length > 0) {
		truckLineItemsWorksheet.columns = Object.keys(truckOrderLineItemInfo[0] ?? {}).map((col) => {
			return { key: col, header: col };
		});
		truckLineItemsWorksheet.addRows(truckOrderLineItemInfo);
	}

	try {
		workbook.xlsx.writeBuffer().then((data) => {
			const blob = new Blob([data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
			});
			FileSaver.saveAs(
				blob,
				getFileNameBasedOnCurrentDateTime("Truck_" + (truck?.truckName ?? "Truck") + "_") + ".xlsx"
			);
		});
	} catch (e) {
		console.error("Error trying to generate order details xlsx file", e);
	}
}
